import {
  IsDateString,
  IsIn,
  IsInt,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Matches,
  Max,
  MaxLength,
  Min,
} from 'class-validator';

/**
 * Officer safety — lone-worker check-in timers, SOS, and property hazard flags.
 *
 * - Off unless the municipality turns it on (`officerSafetyEnabled`, default
 *   false). While off, the portal and the app show no safety control and the
 *   API refuses every write with `safety_disabled`.
 * - The server owns the escalation clock. The phone schedules a local reminder
 *   as a courtesy, but whether supervisors are alerted never depends on it.
 * - Locations are the officer's last known fix, kept no longer than the
 *   municipality keeps live-tracking positions.
 * - None of these shapes is served by the public API or the Client Portal.
 */

/** Tunables served by the API so the clients never hard-code them. */
export const SAFETY_CONFIG = {
  /** The durations offered when starting a timer, in minutes. */
  presetMinutes: [15, 30, 45, 60, 90, 120] as readonly number[],
  /** The extensions offered at a check-in, in minutes. */
  extendMinutes: [10, 15, 30, 60] as readonly number[],
  minMinutes: 5,
  maxMinutes: 480,
  /** The phone reminds the officer this long before the timer is due. */
  reminderLeadMinutes: 5,
  /** How long the SOS control must be held before it asks to confirm. */
  sosHoldMs: 2000,
} as const;

export type SafetyConfig = typeof SAFETY_CONFIG;

/** Error codes a refused safety request answers with. */
export type SafetyRefusal = 'safety_disabled' | 'timer_open' | 'timer_closed';

export const SAFETY_GRACE_MIN = 0;
export const SAFETY_GRACE_MAX = 60;
export const SAFETY_ESCALATION_MIN = 2;
export const SAFETY_ESCALATION_MAX = 60;

// ── Timers ──────────────────────────────────────────────────────────────────

export type SafetyTimerStatus = 'active' | 'overdue' | 'closed';
export type SafetyTimerCloseReason =
  'officer' | 'visit_completed' | 'visit_cancelled' | 'resolved' | 'disabled';

export interface SafetyFix {
  lat: number;
  lng: number;
  accuracy: number | null;
  at: string;
}

export interface SafetyTimerView {
  id: string;
  officerId: string;
  officerName: string;
  localGovernmentId: string;
  fileId: string;
  fileReference: string;
  address: string;
  visitId: string | null;
  status: SafetyTimerStatus;
  startedAt: string;
  expectedMinutes: number;
  dueAt: string;
  /** When supervisors are alerted if nobody checks in: `dueAt` plus grace. */
  alertAt: string;
  lastCheckInAt: string | null;
  lastFix: SafetyFix | null;
  closedAt: string | null;
  closeReason: SafetyTimerCloseReason | null;
}

/** Optional fix carried with a start, check-in or SOS. */
class FixFields {
  @IsOptional()
  @IsNumber()
  @Min(-90)
  @Max(90)
  lat?: number | null;

  @IsOptional()
  @IsNumber()
  @Min(-180)
  @Max(180)
  lng?: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(100_000)
  accuracy?: number | null;

  /** Device clock at the fix. Defaults to the server's receipt time. */
  @IsOptional()
  @IsDateString()
  locatedAt?: string | null;
}

/** `POST /safety/timers`. */
export class StartSafetyTimerDto extends FixFields {
  @IsUUID()
  fileId!: string;

  @IsOptional()
  @IsUUID()
  visitId?: string | null;

  @IsInt()
  @Min(SAFETY_CONFIG.minMinutes)
  @Max(SAFETY_CONFIG.maxMinutes)
  expectedMinutes!: number;
}

/**
 * `POST /safety/timers/:id/check-in` — "I'm OK". With `extendMinutes` the
 * new due time is now plus that; without, the original duration restarts.
 */
export class SafetyCheckInDto extends FixFields {
  @IsOptional()
  @IsInt()
  @Min(SAFETY_CONFIG.minMinutes)
  @Max(SAFETY_CONFIG.maxMinutes)
  extendMinutes?: number;
}

/** `POST /safety/timers/:id/end` — the officer is done and safe. */
export class EndSafetyTimerDto extends FixFields {}

// ── Events (SOS and missed check-ins) ──────────────────────────────────────

export type SafetyEventKind = 'sos' | 'overdue';
export type SafetyEventStatus = 'open' | 'acknowledged' | 'resolved';
export type SafetyLocationSource = 'device' | 'timer' | 'tracking';

export interface SafetyEventView {
  id: string;
  kind: SafetyEventKind;
  status: SafetyEventStatus;
  officerId: string;
  officerName: string;
  localGovernmentId: string;
  fileId: string | null;
  fileReference: string | null;
  address: string | null;
  timerId: string | null;
  raisedAt: string;
  location: (SafetyFix & { source: SafetyLocationSource }) | null;
  note: string | null;
  /** 1 = tier 1 told, 2 = escalated to every supervisor and the safety contact. */
  escalationLevel: number;
  escalatedAt: string | null;
  acknowledgedBy: string | null;
  acknowledgedByName: string | null;
  acknowledgedAt: string | null;
  resolvedBy: string | null;
  resolvedByName: string | null;
  resolvedAt: string | null;
  resolutionNote: string | null;
}

/**
 * `POST /safety/sos`. `clientId` is generated once on the phone when the
 * officer confirms; every retry of the same alarm carries it.
 */
export class RaiseSosDto extends FixFields {
  @IsUUID()
  clientId!: string;

  @IsOptional()
  @IsUUID()
  fileId?: string | null;

  @IsOptional()
  @IsString()
  @MaxLength(500)
  note?: string | null;
}

/** `POST /safety/events/:id/acknowledge`. */
export class AcknowledgeSafetyEventDto {
  @IsOptional()
  @IsString()
  @MaxLength(1000)
  note?: string | null;
}

/** `POST /safety/events/:id/resolve`. A note is required: what happened. */
export class ResolveSafetyEventDto {
  @IsString()
  @MaxLength(1000)
  @Matches(/\S/, { message: 'note must not be empty' })
  note!: string;
}

// ── Status and board ────────────────────────────────────────────────────────

export interface SafetyMunicipality {
  localGovernmentId: string;
  name: string;
  enabled: boolean;
  graceMinutes: number;
  escalationMinutes: number;
}

/** `GET /safety/me` — what the officer's portal or phone needs to show safety controls. */
export interface SafetyStatus {
  municipalities: SafetyMunicipality[];
  /** Whether any municipality the caller works at has safety on. */
  enabled: boolean;
  /** Municipalities where the caller answers alerts (`safety:respond`, safety on). */
  respondsAt: string[];
  timer: SafetyTimerView | null;
  /** The caller's own SOS alarms still open. */
  openSos: SafetyEventView[];
  config: SafetyConfig;
}

/** `GET /safety/local-governments/:id/board`. */
export interface SafetyBoard {
  localGovernmentId: string;
  enabled: boolean;
  graceMinutes: number;
  escalationMinutes: number;
  timers: SafetyTimerView[];
  /** Open and acknowledged events, newest first. */
  events: SafetyEventView[];
  /** Resolved in the last 24 hours, newest first. */
  recent: SafetyEventView[];
}

// ── Hazards ────────────────────────────────────────────────────────────────

export const HAZARD_KINDS = [
  'aggressive_dog',
  'hostile_occupant',
  'structural_danger',
  'biohazard',
  'weapons_history',
  'other',
] as const;
export type HazardKind = (typeof HAZARD_KINDS)[number];

export interface PropertyHazardView {
  id: string;
  fileId: string;
  /** True when the flag was set on a different file for the same property. */
  inherited: boolean;
  sourceFileReference: string | null;
  kind: HazardKind;
  note: string;
  createdBy: string | null;
  createdByName: string | null;
  createdAt: string;
  updatedBy: string | null;
  updatedByName: string | null;
  updatedAt: string;
  reviewOn: string | null;
  /** True once `reviewOn` has passed. The flag is still shown. */
  reviewDue: boolean;
}

/** `GET /files/:fileId/hazards`. */
export interface FileHazards {
  enabled: boolean;
  hazards: PropertyHazardView[];
}

/** One pin's worth of warning for the map. */
export interface HazardMapEntry {
  fileId: string;
  kinds: HazardKind[];
  reviewDue: boolean;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** `POST /files/:fileId/hazards`. */
export class CreateHazardDto {
  @IsIn(HAZARD_KINDS as unknown as string[])
  kind!: HazardKind;

  @IsOptional()
  @IsString()
  @MaxLength(1000)
  note?: string;

  @IsOptional()
  @Matches(ISO_DATE, { message: 'reviewOn must be YYYY-MM-DD' })
  reviewOn?: string | null;
}

/** `PATCH /files/:fileId/hazards/:hazardId`. */
export class UpdateHazardDto {
  @IsOptional()
  @IsIn(HAZARD_KINDS as unknown as string[])
  kind?: HazardKind;

  @IsOptional()
  @IsString()
  @MaxLength(1000)
  note?: string;

  @IsOptional()
  @Matches(ISO_DATE, { message: 'reviewOn must be YYYY-MM-DD' })
  reviewOn?: string | null;
}

/** `POST /files/:fileId/hazards/:hazardId/clear`. */
export class ClearHazardDto {
  @IsOptional()
  @IsString()
  @MaxLength(500)
  reason?: string;
}

/** `PUT /me/push-devices`. */
export class RegisterPushDeviceDto {
  @IsString()
  @MaxLength(200)
  @Matches(/^(ExponentPushToken|ExpoPushToken)\[[^\]]+\]$/, {
    message: 'token must be an Expo push token',
  })
  token!: string;

  @IsOptional()
  @IsIn(['ios', 'android', 'unknown'])
  platform?: 'ios' | 'android' | 'unknown';
}

// ── Realtime payloads ──────────────────────────────────────────────────────

/** `safety.alert` — an event was raised, escalated, acknowledged or resolved. */
export interface SafetyAlertPayload {
  localGovernmentId: string;
  event: SafetyEventView;
}

/** `safety.timer` — a timer started, checked in, went overdue or closed. */
export interface SafetyTimerPayload {
  localGovernmentId: string;
  timer: SafetyTimerView;
}

/** Is a hazard's review date in the past, given today's ISO date? */
export function isReviewDue(reviewOn: string | null, today: string): boolean {
  return !!reviewOn && reviewOn < today;
}
